import React from 'react';
import { useDispatch } from 'react-redux';
import { HotelTileGroupComponent } from '@model/content';
import styled from '@emotion/styled';
import Grid from '@mui/material/Grid';
import { Theme } from '@theme/base';
import { withTheme } from '@emotion/react';
import { FiltersSideBarType } from '@model/filters';
import { OfferType } from '@model/offers/offers';
import { resetSelectedFilters } from '@state/filters';
import { resetDealFinder } from '@state/deal-finder/dealFinderOperations';
import { HotelsLink } from '@components/common/next-link';
import { FromPriceByPathProductTile } from '@components/common/product-tile/from';
import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import { useI18NextContext } from '@components/hooks';
import { ButtonColor, ButtonSize, ButtonVariant, CtaButton } from '@components/material-ui';
import { TestId } from '@components/test-ids';
import { GridCarousel } from '@components/common/grid-carousel';
import { Logging } from '@util/logging';

/* ***************** *
 *       Types       *
 * ***************** */
export interface HotelTileGroupProps extends HotelTileGroupComponent {
  theme?: Theme;
  testId?: string;
  titleSize?: SectionTitleSizes;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div({});

/* ********************************** *
 *    ProductPackageGroupComponent    *
 * ********************************** */

const getMediumTileSizeConfig = (size: number = 0) => {
  switch (size) {
    case 1:
      return 12;
    case 2:
      return 6;
    case 3:
      return 4;
    case 4:
      return 3;
    default:
      return 12;
  }
};

export const HotelTileComponent = (props: HotelTileGroupProps) => {
  const { title, subTitle, ctaTitle, ctaPath, theme, titleSize, hotels, path } = props;
  const t: any = useI18NextContext();
  const dispatch = useDispatch();
  const resetFilters = () => {
    dispatch(resetSelectedFilters(FiltersSideBarType.SEARCH));
    dispatch(resetDealFinder());
  };

  if (!hotels.length) {
    return null;
  }

  const shouldDeepLink = Object.values(OfferType).includes(path as OfferType);

  /* *** RENDERERS *** */
  const productCount = hotels.length;
  const renderProductPackageTiles: () => Array<JSX.Element> = () =>
    hotels
      .map(
        (product: any) =>
          product && (
            <Grid key={product.connectId} item xs={12} sm={6} md={getMediumTileSizeConfig(productCount)}>
              <FromPriceByPathProductTile productPackage={product} deepLink={shouldDeepLink} />
            </Grid>
          )
      )
      .filter((productTile: any) => productTile);

  const productPackageTiles: Array<JSX.Element> = renderProductPackageTiles();

  if (!productPackageTiles || !productPackageTiles.length) {
    Logging.error({
      text: `[ProductPackageGroup][${title}] no valid products in the CMS `
    });
    return null;
  }

  return (
    <Container data-testid={props.testId || TestId.productPackageGroup.main}>
      {title && <SectionTitle heading={title} subHeading={subTitle || ''} size={titleSize} />}
      <GridCarousel container spacing={theme!.custom.gridSpacing.default}>
        {productPackageTiles}
      </GridCarousel>
      {ctaTitle && ctaPath && (
        <HotelsLink path={ctaPath}>
          <CtaButton
            onClick={resetFilters}
            testId={TestId.productPackageGroup.cta}
            label={t(ctaTitle)}
            color={ButtonColor.SECONDARY}
            variant={ButtonVariant.OUTLINED}
            size={ButtonSize.REGULAR}
          />
        </HotelsLink>
      )}
    </Container>
  );
};

export const HotelTileGroup = withTheme(HotelTileComponent);
